import type { DashboardSnapshot, DataService, LiveEvent } from './types'
import type { TenantConfig } from '@/tenant/TenantConfig'
import { TEAMS_FIXTURE } from './mock/fixtures'

const POLL_MS = 30_000

function teamsFallback() {
  return TEAMS_FIXTURE.map((t, i) => ({
    id: t.id,
    name: t.nameCz,
    members: 0,
    ordersHandled: 0,
    csat: 0,
    incidentsResolved: 0,
    rank: i + 1,
  }))
}

function enrichSnapshot(raw: DashboardSnapshot): DashboardSnapshot {
  const teams = raw.teams?.length ? raw.teams : teamsFallback()
  return {
    ...raw,
    teams,
    support: {
      ...raw.support,
      csat: raw.support.csat > 0 ? raw.support.csat : 0,
      nps: raw.support.nps,
      ticketsToday: raw.support.ticketsToday,
      csatHistory: raw.support.csatHistory?.length ? raw.support.csatHistory : [],
    },
  }
}

function diffSnapshots(prev: DashboardSnapshot | null, next: DashboardSnapshot): LiveEvent[] {
  if (!prev) return []
  const out: LiveEvent[] = []
  const at = next.asOf

  if (next.clients.newToday > prev.clients.newToday) {
    out.push({
      id: `evt-new-client-${at}`,
      type: 'new-client',
      at,
      payload: { name: 'Nová registrace', city: '—' },
    })
  }

  const prevIds = new Set(prev.incidents.list.map((i) => i.id))
  for (const inc of next.incidents.list) {
    if (!prevIds.has(inc.id)) {
      out.push({
        id: `evt-inc-open-${inc.id}`,
        type: 'incident-opened',
        at,
        payload: {
          incidentId: inc.id,
          severity: inc.severity,
          location: inc.location,
          category: inc.category,
          title: inc.title,
        },
      })
    }
  }

  if (
    next.revenue.cashflowMonthCzk != null &&
    prev.revenue.cashflowMonthCzk != null &&
    next.revenue.cashflowMonthCzk !== prev.revenue.cashflowMonthCzk
  ) {
    out.push({
      id: `evt-mrr-${at}`,
      type: 'mrr-changed',
      at,
      payload: {
        deltaCzk: next.revenue.cashflowMonthCzk - prev.revenue.cashflowMonthCzk,
        totalCzk: next.revenue.cashflowMonthCzk,
      },
    })
  }

  return out
}

export class ApiDataService implements DataService {
  private snapshot: DashboardSnapshot
  private listeners: Set<(e: LiveEvent) => void> = new Set()
  private snapshotListeners: Set<(s: DashboardSnapshot) => void> = new Set()
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private hasLoadedOnce = false
  private readonly baseUrl: string
  private readonly apiKey: string | undefined

  constructor(tenant: TenantConfig) {
    this.baseUrl = tenant.api.baseUrl.replace(/\/$/, '')
    this.apiKey = tenant.api.apiKey ?? import.meta.env.VITE_DASHBOARD_API_KEY
    this.snapshot = enrichSnapshot({
      asOf: new Date().toISOString(),
      clients: {
        active: 0,
        newThisMonth: 0,
        newToday: 0,
        churnedThisMonth: 0,
        total: 0,
        active7d: 0,
        trial: null,
      newLast14d: 0, inactive7d: 0, wentQuiet7d: 0,
      },
      revenue: {
        mrrCzk: null,
        mrrDeltaPct: null,
        targetCzk: 0,
        arpuCzk: null,
        ltvCzk: null,
        cashflowMonthCzk: null,
        grossMarginPct: null,
        byModule: [], unknownDirectionCount: 0, unknownDirectionCzk: 0,
      },
      churn: { ratePct: 0, lostThisMonth: 0 },
      costs: { fixedMonthlyCzk: 0, categories: [] },
      incidents: {
        open: 0,
        closedToday: 0,
        closedTotal: 0,
        mttrMin: 0,
        daysWithoutP1: 0,
        avgResponseMin: 0,
        list: [],
        categoryBreakdown: [],
        p95ResponseMin: 0,
        storyOfTheDay: null,
      },
      support: { csat: 0, nps: 0, ticketsToday: 0, csatHistory: [] },
      teams: teamsFallback(),
      modules: [],
      topLocations: [],
      pipeline: [],
      onboarding: { startedThisMonth: 0, succeededThisMonth: 0, failedThisMonth: 0 },
      serviceHealth: [],
      venueMap: [],
      venueMapUnplaced: 0,
      venueDirectory: {},
      ordersToday: [],
      ordersMonth: { total: 0, byVenue: [] },
      serviceAdoption: [],
      syncRatio: { success: 0, error: 0, pending: 0, errorPct: null },
      recentActivity: [],
      history: { mrr30d: [], clients30d: [], revenueMonthly: [], incidents14d: [], churn6m: [], margin30d: [] },
    })
  }

  start(): void {
    void this.refresh()
    this.pollTimer = setInterval(() => void this.refresh(), POLL_MS)
  }

  stop(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    this.listeners.clear()
  }

  getSnapshot(): DashboardSnapshot {
    return this.snapshot
  }

  subscribe(handler: (e: LiveEvent) => void): () => void {
    this.listeners.add(handler)
    return () => this.listeners.delete(handler)
  }

  onSnapshot(handler: (snapshot: DashboardSnapshot) => void): () => void {
    this.snapshotListeners.add(handler)
    return () => this.snapshotListeners.delete(handler)
  }

  inject(event: LiveEvent): void {
    this.emit(event)
  }

  simulate(_hours: number): void {}

  private headers(): HeadersInit {
    const h: Record<string, string> = {
      Accept: 'application/json',
      'X-OrderMage-Dashboard-Version': '1',
    }
    if (this.apiKey) {
      h.Authorization = `Bearer ${this.apiKey}`
    }
    return h
  }

  private async refresh(): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/snapshot`, { headers: this.headers() })
      if (!res.ok) {
        console.error('[ApiDataService] snapshot failed', res.status, await res.text())
        return
      }
      const raw = (await res.json()) as DashboardSnapshot
      const next = enrichSnapshot(raw)
      const events = this.hasLoadedOnce ? diffSnapshots(this.snapshot, next) : []
      this.hasLoadedOnce = true
      this.snapshot = next
      for (const h of this.snapshotListeners) {
        try {
          h(next)
        } catch (err) {
          console.error('[ApiDataService] snapshot listener threw', err)
        }
      }
      for (const e of events) {
        this.emit(e)
      }
    } catch (err) {
      console.error('[ApiDataService] refresh error', err)
    }
  }

  private emit(event: LiveEvent): void {
    for (const l of this.listeners) {
      try {
        l(event)
      } catch (err) {
        console.error('[ApiDataService] listener threw', err)
      }
    }
  }
}
